import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, Theme, PaletteMode } from '@mui/material';

/**
 * 主题上下文的数据类型
 */
interface ThemeContextData {
  /**
   * 当前主题模式
   */
  mode: PaletteMode;
  
  /**
   * 切换主题模式的函数
   */
  toggleTheme: () => void;
  
  /**
   * 当前主题对象
   */
  theme: Theme;
}

/**
 * 主题提供者的属性
 */
interface ThemeProviderProps {
  /**
   * 子组件
   */
  children: ReactNode;
}

// 创建主题上下文
const ThemeContext = createContext<ThemeContextData | undefined>(undefined);

/**
 * 获取本地存储的主题模式或使用默认值
 */
const getInitialMode = (): PaletteMode => {
  const savedMode = localStorage.getItem('themeMode');
  return (savedMode === 'dark' || savedMode === 'light') 
    ? savedMode 
    : 'dark';
};

/**
 * 创建亮色主题
 */
const createLightTheme = () => createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2765de',
      light: '#5a8de8',
      dark: '#1a4aa5',
    },
    secondary: {
      main: '#e91e63',
      light: '#f48fb1',
      dark: '#c2185b',
    },
    background: {
      default: '#f8f9fd',
      paper: '#ffffff',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
    },
    info: {
      main: '#0288d1',
      light: '#03a9f4',
      dark: '#01579b',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

/**
 * 创建暗色主题
 */
const createDarkTheme = () => createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#5a8de8',
      light: '#82b1ff',
      dark: '#3874e0',
    },
    secondary: {
      main: '#f06292',
      light: '#f48fb1',
      dark: '#e91e63',
    },
    background: {
      default: '#0d1117',
      paper: '#161b22',
    },
    text: {
      primary: '#e6edf3',
      secondary: '#768390',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    info: {
      main: '#29b6f6',
      light: '#4fc3f7',
      dark: '#0288d1',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        },
      },
    },
  },
});

/**
 * 主题提供者组件
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(getInitialMode);
  const [theme, setTheme] = useState<Theme>(mode === 'light' ? createLightTheme() : createDarkTheme());

  // 切换主题模式
  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // 当主题模式变化时更新主题并保存到本地存储
  useEffect(() => {
    const newTheme = mode === 'light' ? createLightTheme() : createDarkTheme();
    setTheme(newTheme);
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, theme }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

/**
 * 使用主题上下文的自定义Hook
 */
export const useTheme = (): ThemeContextData => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme必须在ThemeProvider内部使用');
  }
  return context;
};
